import React, { useCallback, useEffect, useState } from "react";
import * as api from "./csaApi";

/**
 * Publishing a framework version (design §10a–§10c).
 *
 * Three steps, deliberately not one. A workbook is staged and nothing an audit
 * can see changes; the diff is reviewed; only then can it be activated, and only
 * with a decision for every reworded question.
 *
 * That last gate is the reason this screen exists rather than a single Upload
 * button. Trend comparison matches questions on their `code` across versions, so
 * a question whose wording changed under a stable code would silently continue a
 * chart that is no longer comparing the same thing. Nobody but the person
 * publishing the version knows whether a rewording changed the meaning, so this
 * asks — per question, with both wordings side by side.
 *
 * The default is "different question", because a silently-continued series is
 * the failure that misleads a client, and a default that is safe when ignored is
 * the only kind worth having.
 */
export default function CsaFrameworkPublish({ activeVersion, onActivated }) {
  const [pending, setPending] = useState(null);
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState(null); // validation, many
  const [flash, setFlash] = useState(null);

  /** code -> true (same question) | false (different question) */
  const [decisions, setDecisions] = useState({});
  const [open, setOpen] = useState(null); // which staged version is expanded

  const load = useCallback(() => {
    api
      .adminFrameworkPending()
      .then((r) => setPending(r.pending || []))
      .catch((e) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  /* ---------------- template ---------------- */

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const blob = await api.adminFrameworkTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `csa-framework-${activeVersion || "template"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  /* ---------------- upload ---------------- */

  const send = async () => {
    if (!file || !label.trim()) return;
    setBusy(true);
    setError(null);
    setErrors(null);
    try {
      const r = await api.adminFrameworkUpload(file, label.trim());
      setFile(null);
      setLabel("");
      setDecisions({});
      setOpen(r.version);
      setFlash(`${r.version} staged — ${r.questions} questions. Nothing is live yet.`);
      load();
    } catch (e) {
      // 422 carries every problem in the workbook; show them all.
      if (e.body && Array.isArray(e.body.errors)) {
        setErrors({ list: e.body.errors, total: e.body.error_count });
      } else {
        setError(e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  /* ---------------- activate ---------------- */

  const activate = async (v) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.adminFrameworkActivate(v.version, decisions);
      setFlash(`${r.version} is live. ${r.note}`);
      setDecisions({});
      setOpen(null);
      load();
      if (onActivated) onActivated();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const discard = async (v) => {
    setBusy(true);
    setError(null);
    try {
      await api.adminFrameworkDiscard(v.version);
      setFlash(`${v.version} discarded.`);
      if (open === v.version) setOpen(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  /* ---------------- render ---------------- */

  return (
    <section className="ca-card">
      <div className="ca-card-head">
        <h4>Publish a new version</h4>
      </div>

      <p className="ca-muted">
        Questions are never edited in place. Every audit is pinned to a version,
        so any change to a question produces a new one — which is what makes two
        sites on the same version comparable at all.
      </p>

      {error ? <p className="ca-error">{error}</p> : null}
      {flash ? <p className="ca-flash">{flash}</p> : null}

      {/* ---------------- 1. template ---------------- */}
      <div className="fw-step">
        <span className="fw-n">1</span>
        <div>
          <strong>Start from the current framework</strong>
          <p className="ca-muted">
            The template is generated from the live version, so its columns can
            never drift from what the importer expects. Edit it in Excel.
          </p>
          <button type="button" className="ca-save" disabled={busy} onClick={download}>
            Download template
          </button>
        </div>
      </div>

      {/* ---------------- 2. upload ---------------- */}
      <div className="fw-step">
        <span className="fw-n">2</span>
        <div>
          <strong>Upload it back</strong>
          <p className="ca-muted">
            Checked in full before anything is written. A file with any error is
            rejected entire — never partly applied. Staging changes nothing that
            an audit or a report can see.
          </p>

          <div className="fw-upload">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files[0] || null)}
            />
            <input
              type="text"
              className="fw-label"
              value={label}
              placeholder="Version label, e.g. 01-03-2027"
              maxLength={20}
              onChange={(e) => setLabel(e.target.value)}
            />
            <button
              type="button"
              className="ca-save"
              disabled={busy || !file || !label.trim()}
              onClick={send}
            >
              {busy ? "Checking…" : "Check and stage"}
            </button>
          </div>

          {errors ? (
            <div className="fw-errors">
              <strong>
                Rejected — nothing was changed.{" "}
                {errors.total > errors.list.length
                  ? `Showing ${errors.list.length} of ${errors.total} problems.`
                  : `${errors.total} problem${errors.total === 1 ? "" : "s"}.`}
              </strong>
              <ul>
                {errors.list.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>

      {/* ---------------- 3. review and activate ---------------- */}
      <div className="fw-step">
        <span className="fw-n">3</span>
        <div>
          <strong>Review what changed, then activate</strong>

          {pending === null ? (
            <p className="ca-muted">Loading…</p>
          ) : !pending.length ? (
            <p className="ca-muted">Nothing is staged.</p>
          ) : (
            pending.map((v) => (
              <StagedVersion
                key={v.version}
                v={v}
                expanded={open === v.version}
                onToggle={() => setOpen(open === v.version ? null : v.version)}
                decisions={decisions}
                setDecisions={setDecisions}
                busy={busy}
                onActivate={() => activate(v)}
                onDiscard={() => discard(v)}
              />
            ))
          )}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function StagedVersion({ v, expanded, onToggle, decisions, setDecisions, busy, onActivate, onDiscard }) {
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const d = v.diff;
  const reworded = d.text_changed || [];

  const decided = reworded.filter((q) => typeof decisions[q.code] === "boolean").length;
  const ready = decided === reworded.length;

  return (
    <div className="fw-staged">
      <div className="fw-staged-head" onClick={onToggle}>
        <span className="ca-caret">{expanded ? "▾" : "▸"}</span>
        <strong>{v.version}</strong>
        <span className="ca-badge">{v.questions} questions</span>
        <span className="fw-chips">
          {d.counts.added ? <span className="fw-chip add">+{d.counts.added} new</span> : null}
          {d.counts.removed ? <span className="fw-chip del">−{d.counts.removed} removed</span> : null}
          {d.counts.text_changed ? <span className="fw-chip word">{d.counts.text_changed} reworded</span> : null}
          {d.counts.moved ? <span className="fw-chip move">{d.counts.moved} moved</span> : null}
          {!d.counts.added && !d.counts.removed && !d.counts.text_changed && !d.counts.moved ? (
            <span className="fw-chip">identical to the live version</span>
          ) : null}
        </span>
      </div>

      {expanded ? (
        <div className="fw-body">
          {/* ---------------- consequences ---------------- */}
          <div className="ca-note">
            <strong>What activating this does</strong>
            <ul>
              {(v.consequences || []).map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </div>

          {/* ---------------- the decisions ---------------- */}
          {reworded.length ? (
            <>
              <h5 className="fw-h">
                Reworded questions — {decided} of {reworded.length} decided
              </h5>
              <p className="ca-muted">
                These kept their code, so a trend chart would join across them
                unless you say otherwise. Read both wordings: if the question is
                asking the same thing more clearly, the series continues. If it
                is asking something different, the series breaks here rather than
                comparing two different questions.
              </p>

              {reworded.map((q) => (
                <div
                  className={"fw-q" + (typeof decisions[q.code] === "boolean" ? " done" : "")}
                  key={q.code}
                >
                  <div className="fw-q-head">
                    <span className="ca-badge">{q.code}</span>
                    <span className="ca-muted">
                      P{q.pillar_no} · {q.sub_pillar}
                    </span>
                  </div>

                  <div className="fw-q-texts">
                    <div>
                      <span className="fw-tag">was</span>
                      <p>{q.before}</p>
                    </div>
                    <div>
                      <span className="fw-tag now">now</span>
                      <p>{q.after}</p>
                    </div>
                  </div>

                  <div className="fw-choice">
                    <label>
                      <input
                        type="radio"
                        name={"d-" + v.version + "-" + q.code}
                        checked={decisions[q.code] === true}
                        onChange={() => setDecisions((x) => ({ ...x, [q.code]: true }))}
                      />
                      Same question, clearer wording — keep comparing
                    </label>
                    <label>
                      <input
                        type="radio"
                        name={"d-" + v.version + "-" + q.code}
                        checked={decisions[q.code] === false}
                        onChange={() => setDecisions((x) => ({ ...x, [q.code]: false }))}
                      />
                      Different question — break the series here
                    </label>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <p className="ca-muted">
              No question was reworded, so there is nothing to decide.
            </p>
          )}

          {/* ---------------- added / removed / moved ---------------- */}
          {d.added && d.added.length ? (
            <Detail title={`New questions (${d.added.length})`} rows={d.added} />
          ) : null}
          {d.removed && d.removed.length ? (
            <Detail title={`No longer asked (${d.removed.length})`} rows={d.removed} />
          ) : null}
          {d.moved && d.moved.length ? (
            <div className="fw-detail">
              <h5 className="fw-h">Moved ({d.moved.length})</h5>
              <ul>
                {d.moved.map((m) => (
                  <li key={m.code}>
                    <span className="ca-badge">{m.code}</span> P{m.from.pillar_no}{" "}
                    {m.from.sub_pillar} → P{m.to.pillar_no} {m.to.sub_pillar}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* ---------------- act ---------------- */}
          <div className="fw-actions">
            {confirmDiscard ? (
              <>
                <span className="ca-muted">Discard {v.version} and its questions?</span>
                <button type="button" className="ca-save" disabled={busy}
                        onClick={() => setConfirmDiscard(false)}>
                  Keep it
                </button>
                <button type="button" className="fw-danger" disabled={busy} onClick={onDiscard}>
                  Discard
                </button>
              </>
            ) : (
              <>
                <button type="button" className="fw-plain" disabled={busy}
                        onClick={() => setConfirmDiscard(true)}>
                  Discard
                </button>
                <button
                  type="button"
                  className="ca-save"
                  disabled={busy || !ready}
                  title={ready ? "" : "Every reworded question needs a decision first"}
                  onClick={onActivate}
                >
                  {busy
                    ? "Activating…"
                    : ready
                    ? `Activate ${v.version}`
                    : `${reworded.length - decided} still to decide`}
                </button>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Detail({ title, rows }) {
  return (
    <div className="fw-detail">
      <h5 className="fw-h">{title}</h5>
      <ul>
        {rows.map((r) => (
          <li key={r.code}>
            <span className="ca-badge">{r.code}</span> {r.question}
          </li>
        ))}
      </ul>
    </div>
  );
}
